import {
  IAcceptPartProTradeBid,
  IPartProTradeBid,
  IPartProTradeBidVehicleDetail,
} from "../models/partPro/IPartProTradeBids";

/** Same key the pricing matrix uses for its cells. */
export const matrixCellKey = (partCategoryId: string, accountId: string) =>
  `${partCategoryId}-${accountId}`;

export interface IPendingMatrixBids {
  /** Bids from an account on a category it prices, keyed by matrix cell - highest first */
  byCell: Record<string, IPartProTradeBid[]>;
  /**
   * Bids on a matrix row with no cell of their own: a buyer with no Core account, or an account
   * not set up for that category. Keyed by part category id.
   */
  otherByCategory: Record<string, IPartProTradeBid[]>;
  /** The category (PartPro lot) each waiting bid is on */
  lotIdByBidId: Record<string, string>;
  /** Every bid still waiting for a decision, including categories the matrix doesn't show */
  bidCount: number;
  categoryCount: number;
}

/**
 * Lays a vehicle's PartPro bids over its pricing matrix. Only undecided categories count: an
 * accepted bid is already the cell's price, and a passed one is over.
 */
export function groupPendingMatrixBids(
  detail: IPartProTradeBidVehicleDetail | null | undefined,
  enabledCellKeys: Set<string>
): IPendingMatrixBids {
  const result: IPendingMatrixBids = { byCell: {}, otherByCategory: {}, lotIdByBidId: {}, bidCount: 0, categoryCount: 0 };

  detail?.lots
    .filter((lot) => lot.status === "PENDING" && lot.bids.length > 0)
    .forEach((lot) => {
      result.bidCount += lot.bids.length;
      result.categoryCount += 1;
      lot.bids.forEach((bid) => (result.lotIdByBidId[bid.id] = lot.id));
      if (!lot.partCategoryId) return;
      const categoryId = lot.partCategoryId;

      lot.bids.forEach((bid) => {
        const key = bid.accountId ? matrixCellKey(categoryId, bid.accountId) : null;
        if (key && enabledCellKeys.has(key)) (result.byCell[key] ??= []).push(bid);
        else (result.otherByCategory[categoryId] ??= []).push(bid);
      });
    });

  return result;
}

/** Bids picked to accept, keyed by lot - PartPro awards one bid per category. */
export type BidPicks = Record<string, string>;

/** Where the user changed the default pick, keyed by lot: another bid, or null to leave it out. */
export type BidPickChoices = Record<string, string | null>;

/**
 * The highest bid on each waiting category shown on the matrix - what gets accepted unless the
 * user picks otherwise. A tie goes to the earlier bid, which is PartPro's order too.
 */
export function highestBidPicks(
  detail: IPartProTradeBidVehicleDetail | null | undefined,
  shownCategoryIds: Set<string>
): BidPicks {
  const picks: BidPicks = {};

  detail?.lots
    .filter((lot) => lot.status === "PENDING" && lot.bids.length > 0)
    .filter((lot) => !!lot.partCategoryId && shownCategoryIds.has(lot.partCategoryId))
    .forEach((lot) => {
      picks[lot.id] = lot.bids.reduce((best, bid) => (bid.amount > best.amount ? bid : best)).id;
    });

  return picks;
}

/**
 * The picks to accept: the highest bids with the user's choices laid over them. A choice whose
 * bid was withdrawn, or whose category was decided, since it was made is dropped.
 */
export function resolveBidPicks(highest: BidPicks, choices: BidPickChoices, pending: IPendingMatrixBids): BidPicks {
  const picks: BidPicks = {};

  Object.entries(highest).forEach(([lotId, bidId]) => {
    if (pending.lotIdByBidId[bidId] === lotId) picks[lotId] = bidId;
  });

  Object.entries(choices).forEach(([lotId, bidId]) => {
    if (bidId === null) delete picks[lotId];
    else if (pending.lotIdByBidId[bidId] === lotId) picks[lotId] = bidId;
  });

  return picks;
}

/** Clicking a bid: picks it in place of its category's current pick, or un-picks it. */
export function toggleBidPick(picks: BidPicks, choices: BidPickChoices, lotId: string, bidId: string): BidPickChoices {
  return { ...choices, [lotId]: picks[lotId] === bidId ? null : bidId };
}

/** True when the picks are exactly the highest bids - nothing changed or left out. */
export function isHighestPicks(picks: BidPicks, highest: BidPicks): boolean {
  const lots = Object.keys(highest);
  return lots.length === Object.keys(picks).length && lots.every((lotId) => picks[lotId] === highest[lotId]);
}

export const picksToAccept = (picks: BidPicks): IAcceptPartProTradeBid[] =>
  Object.entries(picks).map(([lotId, bidId]) => ({ lotId, bidId }));

/** Who a bid is from, preferring the Core account name. */
export const bidderName = (bid: IPartProTradeBid) => bid.accountName ?? bid.buyerCompanyName;
